import { useCallback, useRef } from 'react';

import { noop } from 'helpers/utils';

import useUnmount from './useUnmount';

type ScrollCallback = (isScrolling: boolean) => void;

interface UseScrollDetectionOptions {
  debounceTime?: number;
  eventTypes?: Array<'wheel' | 'touchmove'>;
  passive?: boolean;
}

/**
 * Custom hook to detect scrolling activity on an element
 * @param callback Function called when scrolling state changes
 * @param options Configuration options
 * @returns Object with attach function to connect to an element
 */
export const useScrollDetection = (
  callback: ScrollCallback,
  options: UseScrollDetectionOptions = {}
) => {
  const {
    debounceTime = 300,
    eventTypes = ['wheel', 'touchmove'],
    passive = true,
  } = options;

  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const attachedElementsRef = useRef<HTMLElement[]>([]);

  const handleScroll = useCallback(() => {
    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
      callback(true);
    }

    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }

    scrollTimerRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      callback(false);
      scrollTimerRef.current = null;
    }, debounceTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup function
  useUnmount(() => {
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }

    // Remove event listeners from all attached elements
    attachedElementsRef.current.forEach(element => {
      eventTypes.forEach(eventType => {
        element.removeEventListener(eventType, handleScroll);
      });
    });

    attachedElementsRef.current = [];
  });

  const attachTo = useCallback(
    (
      element: HTMLElement | null,
      attachOptions?: Omit<UseScrollDetectionOptions, 'debounceTime'>
    ) => {
      if (!element) {
        console.warn('DOM element is null');
        return noop;
      }

      const mergedOptions = {
        eventTypes: attachOptions?.eventTypes ?? eventTypes,
        passive: attachOptions?.passive ?? passive,
      };

      attachedElementsRef.current.push(element);

      mergedOptions.eventTypes.forEach(eventType => {
        element.addEventListener(eventType, handleScroll, {
          passive: mergedOptions.passive,
        });
      });

      return () => {
        mergedOptions.eventTypes.forEach(eventType => {
          element.removeEventListener(eventType, handleScroll);
        });

        attachedElementsRef.current = attachedElementsRef.current.filter(
          el => el !== element
        );
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { attachTo };
};
